import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Beer, Ticket, Clock, CheckCircle2, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function daysLeft(expiresAtIso) {
  if (!expiresAtIso) return null;
  const exp = new Date(expiresAtIso);
  const diff = exp - new Date();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function PerksCard({ memberId }) {
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/drink-tickets/my?member_id=${memberId}`);
      setActive(res.data.active || []);
      setHistory(res.data.history || []);
    } catch {
      // silent — don't spam toasts on profile load
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (active.length === 0 && history.length === 0) return null;

  return (
    <Card className="bg-card border-white/10" data-testid="perks-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Ticket className="w-5 h-5 text-amber-400" />
          Perks & Rewards
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {active.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No active perks. Sign up new friends or join a tournament to earn a free drink ticket.
          </p>
        ) : (
          active.map((t) => {
            const dLeft = daysLeft(t.expires_at);
            return (
              <button
                key={t.id}
                onClick={() => navigate(`/tickets/${t.id}`)}
                className="w-full text-left group relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 hover:border-amber-500/60 transition-all"
                data-testid={`perk-ticket-${t.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                    <Beer className="w-6 h-6 text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">Free Drink Ticket</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{t.issue_reason === 'signup' ? 'Welcome bonus' : 'Tournament perk'}</span>
                      <span className="opacity-40">•</span>
                      <Clock className="w-3 h-3" />
                      <span>
                        {dLeft === 0
                          ? 'Expires today'
                          : dLeft === 1
                            ? '1 day left'
                            : `${dLeft} days left`}
                      </span>
                    </p>
                    {t.sponsor_name && (
                      <p className="text-[11px] text-amber-300/80 mt-0.5">
                        Sponsored by {t.sponsor_name}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-amber-400/60 group-hover:text-amber-300 shrink-0" />
                </div>
              </button>
            );
          })
        )}

        {history.length > 0 && (
          <div className="pt-3 border-t border-white/5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Redeemed / expired</p>
            <div className="space-y-1.5">
              {history.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`history-ticket-${t.id}`}>
                  {t.status === 'redeemed' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-secondary" />
                  ) : (
                    <Clock className="w-3.5 h-3.5 text-zinc-500" />
                  )}
                  <span className="capitalize">{t.status}</span>
                  <span className="opacity-40">•</span>
                  <span>{new Date(t.redeemed_at || t.expires_at || t.issued_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PerksCard;
